<script setup lang="ts">
import { SharedData } from '@/@types'
import { Ticket, Workshop } from '@/@types/workshop'
import { Button } from '@/components/ui/button'
import { Title } from '@/components/ui/title'
import { useTicketCart } from '@/composables/useTicketCart'
import { formatPrice } from '@/helpers/current-price'
import { router, usePage } from '@inertiajs/vue3'
import { ShoppingCart } from 'lucide-vue-next'
import GroupDiscountBanner from './GroupDiscountBanner.vue'
import TicketSelector from './TicketSelector.vue'

defineProps<{
    tickets: Ticket[]
    workshop: Workshop
}>()

const { user } = usePage<SharedData>().props

const { price, ticketCart, total, totalToDiscount, updateTotal } = useTicketCart()
</script>

<template>
    <section class="relative z-20 mt-12 min-h-150 rounded-[20px] bg-[#0e0f12] px-0" id="tickets">
        <div class="relative flex flex-col items-center justify-center gap-6 overflow-hidden pt-px">
            <div
                class="after:bg-linear after:via-primary absolute inset-0 after:absolute after:top-1/2 after:left-1/2 after:aspect-square after:min-h-[200%] after:min-w-[200%] after:-translate-x-1/2 after:-translate-y-1/2 after:bg-linear-to-r after:from-transparent after:from-30% after:via-50% after:to-transparent after:to-70% after:content-['']"
            ></div>
        </div>
        <div class="flex flex-col items-center justify-between gap-6 px-4 pt-4 pb-4 text-center md:flex-row md:pt-8 md:pb-12 md:text-left lg:pt-16">
            <div class="flex flex-col gap-3">
                <Title>{{ $t('Acquire your ingress') }}</Title>
            </div>

            <div class="flex items-center gap-3">
                <ShoppingCart class="size-6" />
                {{ formatPrice(price.finalPrice) }}
            </div>
        </div>

        <div class="relative flex flex-col items-center justify-center gap-6 overflow-hidden pt-px">
            <div class="relative flex flex-col items-center justify-center gap-2 overflow-hidden bg-[#0e0f12] pt-2">
                <GroupDiscountBanner v-if="workshop.any_active_promotion" />

                <div class="relative z-20 mt-3 grid gap-4 lg:grid-cols-3">
                    <TicketSelector v-for="ticket in tickets" :key="ticket.id" :ticket="ticket" @update-total="updateTotal" />
                </div>
            </div>
        </div>
    </section>

    <Teleport to="body">
        <Transition
            enter-active-class="transition-opacity transition-transform duration-300 ease-out"
            enter-from-class="opacity-0 translate-y-5"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-opacity transition-transform duration-200 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-5"
        >
            <form
                v-if="total > 0"
                @submit.prevent="
                    router.post(user ? '/cart/add_items' : '/session_cart/add_items', {
                        cart: {
                            items: ticketCart,
                        },
                    })
                "
                class="fixed bottom-3 left-1/2 z-50 flex w-full max-w-2xl -translate-x-1/2 items-center justify-between gap-4 gap-x-3 rounded-full bg-[#1C2229] px-4 py-3 md:bottom-12 md:gap-x-8 lg:w-fit lg:py-2.5"
            >
                <div class="relative z-80 flex flex-1 items-center gap-3 font-medium text-white lg:items-center">
                    <ShoppingCart class="size-6 min-w-6" />
                    <div class="flex flex-1 flex-col gap-x-3 lg:flex-row lg:items-center lg:justify-between lg:gap-x-8">
                        <data :value="formatPrice(price.finalPrice)" class="tabular-nums">{{ formatPrice(price.finalPrice) }}</data>

                        <span class="text-xs">
                            <span v-show="totalToDiscount > 0">
                                {{ $t('tickets left for', { quantity: totalToDiscount }) }}
                                <strong class="text-primary font-semibold">{{ $t('10% discount') }}</strong>
                            </span>

                            <span v-show="totalToDiscount <= 0" class="text-green-500">
                                <strong class="font-semibold text-green-500">
                                    {{ $t('10% discount') }} (- <span class="tabular-nums">{{ formatPrice(price.discount) }} </span>)
                                </strong>
                            </span>
                        </span>
                    </div>
                </div>

                <Button class="rounded-full">{{ $t('Finish order') }}</Button>
            </form>
        </Transition>
    </Teleport>
</template>
